import React from 'react';
import NeapolitanCard from './NeapolitanCard';
import {
  playerName,
  isPartner,
  sideLabel,
  teamNames,
  orderedSides,
} from '../multiplayer/multiLabels';

// ===== 3-4 player online UI pieces (never used by 2-player rooms or CPU mode) =====

// Other players in turn order after you: name, team, card COUNT only, presence.
export function PlayersRow({ view }) {
  const seats = view.players.map((p) => p.seat);
  const start = seats.indexOf(view.you);
  const others = [...seats.slice(start + 1), ...seats.slice(0, start)].map((s) =>
    view.players.find((p) => p.seat === s)
  );
  return (
    <ul className="mt-3 flex items-stretch justify-center gap-2" data-testid="online-players-row" aria-label="Altri giocatori">
      {others.map((p) => (
        <PlayerChip key={p.seat} view={view} p={p} />
      ))}
    </ul>
  );
}

function PlayerChip({ view, p }) {
  const name = playerName(view, p.seat);
  const partner = isPartner(view, p.seat);
  const active = view.phase === 'playing' && view.turn === p.seat;
  const team = view.numPlayers === 4 ? (partner ? 'tuo compagno di squadra' : 'squadra avversaria') : 'avversario';
  const said = `${name}, ${team}, ${p.cardCount} ${p.cardCount === 1 ? 'carta' : 'carte'} in mano, ${p.online ? 'collegato' : 'non collegato'}${active ? ', sta giocando' : ''}.`;
  return (
    <li
      className={`flex-1 min-w-0 rounded-xl px-2 py-1.5 border transition-colors ${
        active ? 'border-[#D4AF37] bg-[#D4AF37]/15' : 'border-white/10 bg-black/25'
      }`}
      data-testid={`online-player-${p.seat}`}
    >
      <span className="sr-only">{said}</span>
      <div aria-hidden="true">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className={`w-2 h-2 rounded-full shrink-0 ${p.online ? 'bg-emerald-400' : 'bg-red-400'}`} />
          <span className="font-sans text-xs font-semibold text-[#FDFBF7] break-all leading-tight">{name}</span>
        </div>
        <p className={`font-sans text-[10px] mt-0.5 ${partner ? 'text-[#D4AF37]' : 'text-[#E0E0D1]/60'}`}>
          {view.numPlayers === 4 ? (partner ? 'Compagno' : 'Avversario') : `Giocatore ${'ABCD'.indexOf(p.seat) + 1}`}
          {!p.online && ' · non collegato'}
        </p>
        <div className="flex items-center gap-0.5 mt-1 h-8">
          {Array.from({ length: p.cardCount }).map((_, i) => (
            <NeapolitanCard key={i} faceDown sizeClass="w-5" testId={`online-player-${p.seat}-card-${i}`} />
          ))}
        </div>
      </div>
    </li>
  );
}

// Match score: one row per player (3) or per team (4), your side first.
export function MultiScorePanel({ view }) {
  const sides = orderedSides(view);
  const label = (s) => (view.numPlayers === 4 ? `${sideLabel(view, s)} (${teamNames(view, s)})` : sideLabel(view, s));
  const said = `Punteggio partita. ${sides.map((s) => `${label(s)} ${view.sideScores[s]}`).join('. ')}. Si vince a 11.`;
  return (
    <div className="w-full rounded-xl bg-[#2C1A14]/80 border border-[#D4AF37]/25 px-3 py-2" tabIndex={0} aria-label={said} data-testid="match-score">
      {sides.map((s) => (
        <div key={s} className="flex items-center justify-between gap-2" data-testid={`side-score-${s}`}>
          <span className="font-sans text-xs uppercase tracking-[0.1em] text-[#E0E0D1]/80 break-words min-w-0">{label(s)}</span>
          <span className={`font-serif text-xl font-bold ${s === view.yourSide ? 'text-[#D4AF37]' : 'text-[#FDFBF7]'}`}>{view.sideScores[s]}</span>
        </div>
      ))}
    </div>
  );
}

const ROWS = [
  ['Scope', (d) => d.scope, (d) => d.scope > 0],
  ['Carte', (d) => d.carte, (d) => d.won.carte],
  ['Denari', (d) => d.denari, (d) => d.won.denari],
  ['Settebello', (d) => (d.settebello ? 'Sì' : 'No'), (d) => d.won.settebello],
  ['Primiera', (d) => d.primiera, (d) => d.won.primiera],
];

// End-of-hand detail: a column per side.
export function MultiBreakdown({ view }) {
  const b = view.lastHandBreakdown;
  const sides = orderedSides(view);
  return (
    <table className="w-full text-sm mt-2" data-testid="online-multi-breakdown">
      <caption className="sr-only">Dettaglio punti della mano</caption>
      <thead>
        <tr>
          <th scope="col" className="sr-only">Voce</th>
          {sides.map((s) => (
            <th key={s} scope="col" className="font-sans text-[10px] uppercase tracking-wider text-[#E0E0D1]/70 font-normal pb-1">
              {sideLabel(view, s)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {ROWS.map(([label, val, won]) => (
          <tr key={label} className="border-b border-white/5">
            <th scope="row" className="font-sans text-xs uppercase tracking-[0.1em] text-[#E0E0D1]/60 font-normal text-left py-1">{label}</th>
            {sides.map((s) => (
              <td key={s} className={`text-center font-sans ${won(b[s]) ? 'text-[#D4AF37] font-bold' : 'text-[#E0E0D1]'}`}>{val(b[s])}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// "Chiudi la partita" with an inline confirmation step (3-4 players only).
// The server re-checks the 2-minute absence; this only offers the action.
export function CloseGameControl({ onConfirm, testPrefix = 'close-game' }) {
  const [asking, setAsking] = React.useState(false);
  const confirmRef = React.useRef(null);
  const openRef = React.useRef(null);
  React.useEffect(() => {
    if (asking) confirmRef.current?.focus();
  }, [asking]);
  const btn =
    'rounded-full font-sans font-semibold text-sm px-4 py-2 transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/50';
  if (!asking) {
    return (
      <button type="button" ref={openRef} onClick={() => setAsking(true)} data-testid={`${testPrefix}-btn`} className={`${btn} mt-2 bg-[#7A1F1F] text-[#FDFBF7] hover:bg-[#932828]`}>
        Chiudi la partita
      </button>
    );
  }
  return (
    <div className="mt-2 flex flex-col items-center gap-2" data-testid={`${testPrefix}-confirm`}>
      <p className="font-sans text-xs text-[#FDFBF7]">Vuoi davvero chiudere la partita per tutti?</p>
      <div className="flex gap-2">
        <button type="button" ref={confirmRef} onClick={() => { setAsking(false); onConfirm(); }} data-testid={`${testPrefix}-yes`} className={`${btn} bg-[#7A1F1F] text-[#FDFBF7] hover:bg-[#932828]`}>
          Sì, chiudi la partita
        </button>
        <button type="button" onClick={() => { setAsking(false); setTimeout(() => openRef.current?.focus(), 0); }} data-testid={`${testPrefix}-no`} className={`${btn} bg-[#2C1A14] border border-[#D4AF37]/40 text-[#FDFBF7]`}>
          Annulla
        </button>
      </div>
    </div>
  );
}
